import React, { useEffect, useId, useRef, useState } from 'react';
import { apiPatch, apiPost } from '../api';
import type { FeatureResponse } from './FeatureResponseView';

const MAX_RESPONSE = 2000;
// PNG and JPEG only, as the update email's screenshots. The extension is fixed by type
// because the upload route takes it from the file name sent, and nothing from the
// admin's own file name should decide it.
const EXTENSION: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg' };
const MAX_BYTES = 5 * 1024 * 1024;

export interface ResponseDraft { text: string; imageUrl: string; }

interface Props {
  featureId: string;
  title: string;
  response?: FeatureResponse;
  draft?: ResponseDraft;
  onDraftChange?: (draft: ResponseDraft) => void;
  onSaved: (response: FeatureResponse | null) => void;
  onCancel: () => void;
}

// The admin's editor for a checklist request's developer update (#1008). Uploads go
// through the help editor's presigned route into help-media/, which site deploys never
// delete. Nothing reaches the board until Save.
const FeatureResponseEditor: React.FC<Props> = ({ featureId, title, response, draft, onDraftChange, onSaved, onCancel }) => {
  const [text, setText] = useState(draft?.text ?? response?.text ?? '');
  const [imageUrl, setImageUrl] = useState(draft?.imageUrl ?? response?.imageUrl ?? '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const textId = useId();
  // A tab switch unmounts the editor, so the page keeps the draft and hands it back here.
  const draftChanged = useRef(onDraftChange);
  draftChanged.current = onDraftChange;
  useEffect(() => { draftChanged.current?.({ text, imageUrl }); }, [text, imageUrl]);
  // Only a newly opened editor takes focus; one coming back with a draft must not steal it.
  const reopened = useRef(!!draft);
  useEffect(() => { if (!reopened.current) textRef.current?.focus(); }, []);
  const busy = uploading || saving;

  const upload = async (file: File) => {
    setError('');
    const ext = EXTENSION[file.type];
    if (!ext) { setError('Use a PNG or JPEG.'); return; }
    if (file.size > MAX_BYTES) { setError('That file is over 5 MB. Save a smaller copy and try again.'); return; }
    setUploading(true);
    try {
      const res = await apiPost('/admin/articles/upload-url', { slug: `feature-${featureId}`, filename: `shot.${ext}`, contentType: file.type });
      const { uploadUrl, imageUrl: uploaded } = await res.body.json() as unknown as { uploadUrl: string; imageUrl: string };
      if (typeof uploadUrl !== 'string' || !uploadUrl || typeof uploaded !== 'string' || !uploaded) {
        throw new Error('the upload route returned no address');
      }
      const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!put.ok) throw new Error(`upload refused: ${put.status}`);
      setImageUrl(uploaded);
    } catch (e) {
      console.error('developer update screenshot upload failed', e);
      setError('Upload failed, so nothing was added. Try again.');
    } finally {
      setUploading(false);
    }
  };

  // On success the page closes this editor, so saving is only reset on failure.
  const send = async (body: Record<string, unknown>) => {
    setError('');
    setSaving(true);
    try {
      const res = await apiPatch(`/admin/feature-requests/${featureId}`, body);
      const data = await res.body.json() as unknown as { response: FeatureResponse | null };
      onSaved(data.response ?? null);
    } catch (e) {
      console.error('saving the developer update failed', e);
      setError("Couldn't save the response. Try again.");
      setSaving(false);
    }
  };

  const save = () => send({ response: { text, ...(imageUrl ? { imageUrl } : {}) } });

  const remove = () => {
    if (!window.confirm('Delete this response? It comes off the board right away.')) return;
    void send({ response: null });
  };

  return (
    <div className="feature-response feature-response--editing">
      <label className="feature-response-label" htmlFor={textId}>Developer update</label>
      <textarea
        ref={textRef}
        id={textId}
        className="feature-response-input"
        value={text}
        maxLength={MAX_RESPONSE}
        rows={5}
        disabled={saving}
        onChange={e => setText(e.target.value.slice(0, MAX_RESPONSE))}
      />
      <p className="feature-response-count">{text.length} / {MAX_RESPONSE}</p>
      <div className="feature-response-shot-row">
        {imageUrl && <img className="feature-response-preview" src={imageUrl} alt={`Screenshot for ${title}`} />}
        <input
          ref={input}
          type="file"
          aria-label="Screenshot file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }}
        />
        <button type="button" className="btn-secondary" onClick={() => input.current?.click()} disabled={busy}>
          {uploading ? 'Uploading…' : imageUrl ? 'Replace screenshot' : 'Add screenshot'}
        </button>
        {imageUrl && (
          <button type="button" className="btn-secondary" onClick={() => setImageUrl('')} disabled={busy}>
            Remove screenshot
          </button>
        )}
      </div>
      <p className="feature-response-note">
        Anyone with the link can open this screenshot. Leave out customer names, email addresses, book text and anything else private.
      </p>
      <div className="feature-response-actions">
        {response && (
          <button type="button" className="btn-secondary feature-response-delete" onClick={remove} disabled={busy}>
            Delete response
          </button>
        )}
        <div className="feature-response-actions-end">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => void save()} disabled={busy || !text.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {error && <p className="feature-response-error" role="alert">{error}</p>}
    </div>
  );
};

export default FeatureResponseEditor;
